import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Clock, Lock, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = ({ onLeadSubmit }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[^\d]/g, "");
    return cleaned.length >= 10;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleConsentChange = (checked) => {
    setFormData((prev) => ({ ...prev, consent: checked }));
    if (errors.consent) {
      setErrors((prev) => ({ ...prev, consent: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.full_name || formData.full_name.trim().length < 2) {
      newErrors.full_name = "Please enter your full name";
    }
    if (!formData.email || !validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone || !validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number (at least 10 digits)";
    }
    if (!formData.consent) {
      newErrors.consent = "You must consent to be contacted to proceed";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads`, formData);
      toast.success("Information submitted successfully!");
      onLeadSubmit(response.data.id, {
        name: formData.full_name,
        email: formData.email
      });
      navigate("/quote");
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, #F5F5F5, #FFFFFF, #FAFAFA)' }}>
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/financial-flight-logo.png" 
              alt="Financial Flight" 
              className="w-12 h-12"
            />
            <div>
              <span className="text-2xl font-bold" style={{ color: '#2E2E2E' }}>Financial Flight</span>
              <p className="text-xs" style={{ color: '#D4AF37' }}>S.O.A.R.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a 
              href="https://financialflight-taxservices.com/" 
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-semibold rounded-lg transition-all hover:opacity-90"
              style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}
            >
              Tax Services
            </a>
            <Link 
              to="/intake" 
              className="px-4 py-2 text-sm font-semibold rounded-lg transition-all hover:opacity-90"
              style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}
            >
              For Insurance Agents
            </Link>
            <Link 
              to="/admin/login" 
              className="text-sm text-gray-600 hover:opacity-80 transition-opacity"
              data-testid="admin-login-link"
            >
              Admin Login
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ color: '#2E2E2E' }}>
                Get Your Life Insurance Quote in{" "}
                <span style={{ color: '#D4AF37' }}>60 Seconds</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                Financial Flight helps individuals and families get fast, accurate life insurance quotes 
                so they can protect what matters most. We simplify the entire process—no jargon, no pressure—just 
                real numbers, real options, and real guidance.
              </p>
              <p className="text-sm font-semibold" style={{ color: '#D4AF37' }}>
                S.O.A.R. — Securing Our Assets Responsibly
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <Clock className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#D4AF37' }} />
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: '#2E2E2E' }}>Fast & Easy</h3>
                  <p className="text-sm text-gray-600">Get quotes in under a minute</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <Lock className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#D4AF37' }} />
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: '#2E2E2E' }}>Secure & Private</h3>
                  <p className="text-sm text-gray-600">Your data is protected</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <TrendingUp className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#D4AF37' }} />
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: '#2E2E2E' }}>Best Rates</h3>
                  <p className="text-sm text-gray-600">Current market averages</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <Shield className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#D4AF37' }} />
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: '#2E2E2E' }}>No Obligation</h3>
                  <p className="text-sm text-gray-600">Just estimates, no pressure</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-2xl">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#2E2E2E' }}>Start Your Free Quote</h2>
              <p className="text-sm text-gray-600">Enter your information to get started</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-capture-form">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-sm font-medium text-gray-700">
                  Full Name *
                </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="h-11"
                  data-testid="full-name-input"
                />
                {errors.full_name && (
                  <p className="text-sm text-red-600" data-testid="full-name-error">{errors.full_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-11"
                  data-testid="email-input"
                />
                {errors.email && (
                  <p className="text-sm text-red-600" data-testid="email-error">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-11"
                  data-testid="phone-input"
                />
                {errors.phone && (
                  <p className="text-sm text-red-600" data-testid="phone-error">{errors.phone}</p>
                )}
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg border border-gray-300" style={{ backgroundColor: '#FFF9E6' }}>
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={handleConsentChange}
                  className="mt-0.5"
                  data-testid="consent-checkbox"
                />
                <label
                  htmlFor="consent"
                  className="text-sm text-gray-700 leading-relaxed cursor-pointer"
                >
                  I consent to be contacted by Financial Flight or its partners regarding my insurance quote request. *
                </label>
              </div>
              {errors.consent && (
                <p className="text-sm text-red-600" data-testid="consent-error">{errors.consent}</p>
              )}

              <Button
                type="submit"
                disabled={loading || !formData.consent}
                className="w-full h-12 text-base font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed transition-all hover:opacity-90"
                style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}
                data-testid="submit-lead-button"
              >
                {loading ? "Submitting..." : "Get My Free Quote"}
              </Button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              By submitting, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-sm text-center text-gray-600 mb-6">
          Our estimates are based on recent data from trusted sources:
        </p>
        <div className="flex flex-wrap justify-center items-center gap-8 text-gray-500">
          <span className="text-sm font-medium">Progressive</span>
          <span className="text-sm font-medium">Guardian Life</span>
          <span className="text-sm font-medium">Aflac</span>
          <span className="text-sm font-medium">Policygenius</span>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;